import { IEventLoggingComponentProps, withEventLogging } from '@AppModels/Logging';

import {
	CampaignViewModel,
	FinancialBrokerDealer,
	IContact,
	IEmbeddedSocialMediaTemplateContent,
	IOperationResultNoValue,
	IRichContentEditorState,
	ISuggestedSendSchedule,
	ITemplate,
	ITemplateStatus,
	Industry,
	SocialMediaType,
	TemplateStatus,
	TemplateType,
	createRawRichTextContentStateWithText,
	excludeKeysOf,
} from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { computed, makeObservable, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import { parse as parseQueryStringParams } from 'query-string';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import {
	IImpersonationContextComponentProps,
	ILocationState,
	IModalContext,
	ImpersonationContextKey,
	ModalChildComponentContextKey,
} from '../../../../models';
import {
	CampaignType,
	IAdminUserSessionComponentProps,
	ISystemEmailTemplateWithStatuses,
	ITemplatesIndustryWithCategories,
} from '../../../../models/AdminModels';
import {
	ErrorMessagesViewModelKey,
	FullScreenModalViewModelKey,
	IEnvironmentComponentProps,
	IErrorMessageComponentProps,
	IFullscreenModalComponentProps,
	UserSessionViewModelKey,
} from '../../../../models/AppState';
import { Topics } from '../../../../models/LocalNotificationTopics';
import {
	createRichContentEditorStateWithText,
	getScheduleTimeOptions,
	getSocialMediaPlainTextContentFromRawRichContent,
} from '../../../../models/UiUtils';
import {
	TemplateCategoriesViewModel,
	TemplateEditViewModel,
	TemplateWithStatusesViewModel,
} from '../../../../viewmodels/AdminViewModels';
import { Checkbox } from '../../../../web/components/Checkbox';
import { DeprecatedTextArea } from '../../../../web/components/DeprecatedTextArea';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import {
	INotificationServiceComponentProps,
	withNotificationService,
} from '../../../../web/components/LocalNotificationObserver/WithNotificationService';
import { ISelectBoxOption } from '../../../../web/components/SelectBox';
import { TextInput } from '../../../../web/components/TextInput';
import { PreviewSocialMediaPostModal } from '../../../../web/components/socialMedia/PreviewSocialMediaPostModal';
import { SocialMediaPostContentEditor } from '../../../../web/components/socialMedia/SocialMediaPostContentEditor';
import {
	EditSocialMediaPostContextProvider,
	IEditSocialMediaPostContext,
} from '../../../../web/containers/socialMedia/EditSocialMediaPost/context';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { CitationEditor } from '../CitationEditor';
import { EditCampaignTemplate } from '../EditCampaignTemplate';
import { IEditCampaignTemplateContextProps, withEditCampaignTemplateContext } from '../EditCampaignTemplate/context';
import { ExpirationDisplay } from '../EditEmailTemplate/presentation';
import { styleSheet } from '../EditEmailTemplate/styles';
import { IndustryCategorySection } from '../IndustryCategorySection';

interface IProps
	extends IEventLoggingComponentProps,
		IAdminUserSessionComponentProps,
		IFullscreenModalComponentProps,
		RouteComponentProps<any>,
		IEnvironmentComponentProps,
		IEventLoggingComponentProps,
		IErrorMessageComponentProps,
		IModalContext<ISystemEmailTemplateWithStatuses>,
		INotificationServiceComponentProps<ISystemEmailTemplateWithStatuses>,
		IEditCampaignTemplateContextProps,
		IImpersonationContextComponentProps {
	campaign?: CampaignViewModel;
	className?: string;
	styles?: StyleDeclarationValue[];
}

interface IState {
	campaignExpires?: boolean;
	contentEditorState?: IRichContentEditorState;
	editorLoaded?: boolean;
	featured?: 'all' | 'none' | 'some';
	firstNamePlaceholderPopover?: { x: number; y: number };
	initialIndustryOptions?: ITemplateStatus[];
	keywords?: string;
	loadingContent?: boolean;
	readonly?: boolean;
	redirectTo?: string;
	schedule?: ISuggestedSendSchedule;
	selectedIndustryOptions?: ITemplateStatus[];
	selectedExpirationMoment?: moment.Moment;
	showPreview?: boolean;
	summary?: string;
	testEmail?: IContact;
	firstSelection?: boolean;
	templateLink?: string;
	parentTemplateId?: string;
}

class _EditSocialTemplate extends React.Component<IProps, IState> {
	private mMinutes = 0;
	private campaignEditor: TemplateEditViewModel;
	private mCategoryOptions: ITemplatesIndustryWithCategories[];
	private mEditSocialMediaPostContextRef = React.createRef<IEditSocialMediaPostContext>();

	constructor(props: IProps) {
		super(props);

		makeObservable<
			_EditSocialTemplate,
			| 'mMinutes'
			| 'campaignEditor'
			| 'mCategoryOptions'
			| 'validSelectedHour'
			| 'timeOptions'
			| 'isEditing'
			| 'isFinancialIndustry'
		>(this, {
			mMinutes: observable.ref,
			campaignEditor: observable.ref,
			mCategoryOptions: observable.ref,
			validSelectedHour: computed,
			timeOptions: computed,
			isEditing: computed,
			isFinancialIndustry: computed,
		});

		this.state = {
			contentEditorState: createRichContentEditorStateWithText(''),
			featured: 'none',
			loadingContent: true,
			selectedIndustryOptions: [],
			firstSelection: false,
			templateLink: '',
			parentTemplateId: '',
		};
		this.campaignEditor = new TemplateEditViewModel(props.userSession);
	}

	public async componentDidMount() {
		const { history, logEvent, logApiError, errorMessages, location: loc } = this.props;

		const locationState: ILocationState<TemplateCategoriesViewModel, TemplateWithStatusesViewModel> =
			history?.location.state;
		const { readonly }: { readonly?: boolean } = parseQueryStringParams(loc.search) || {};
		this.setState({ readonly: Boolean(readonly) });
		try {
			const categories = await this.campaignEditor.loadCategories(CampaignType.Social);

			this.mCategoryOptions = categories;

			if (!locationState?.model) {
				this.setState({ loadingContent: false });
				return;
			}
			const templateId = locationState.model?.templateReference?.template?.id;

			logEvent('LoadTemplate', { id: templateId });
			await this.campaignEditor.load(templateId, CampaignType.Social);
			this.onContentLoaded();
			this.setState({
				contentEditorState: createRichContentEditorStateWithText(this.campaignEditor?.template?.citation || ''),
			});
		} catch (error) {
			logApiError('LoadTemplate-Error', error);

			errorMessages.pushApiError(error);
		}
	}

	private onContentLoaded = () => {
		const selectedIndustryOptions: ITemplateStatus[] = this.campaignEditor.statuses.filter(
			x => x.category !== 'Archived' && x.category !== 'Featured'
		);

		const numberOfFeatured = this.campaignEditor.statuses?.filter(x => x.category === 'Featured')?.length;

		this.mEditSocialMediaPostContextRef.current?.setImages(this.campaignEditor?.template?.attachments || []);

		this.mEditSocialMediaPostContextRef.current?.setContent(
			getSocialMediaPlainTextContentFromRawRichContent(this.campaignEditor?.template?.content)
		);

		const selectedExpirationMoment = this.campaignEditor?.template?.schedule?.expirationDate
			? moment(this.campaignEditor.template.schedule.expirationDate)
			: null;

		this.mMinutes = selectedExpirationMoment ? selectedExpirationMoment.minutes() : 0;

		this.props.editCampaignTemplateContext?.setDisplayName(this.campaignEditor.template.name);
		this.setState({
			campaignExpires: this.campaignEditor?.template?.schedule?.expirationDate ? true : false,
			// check if half of the statuses are featured, if so, all "selected" categories are featured
			featured:
				numberOfFeatured === this.campaignEditor.statuses?.length / 2
					? 'all'
					: numberOfFeatured === 0 || !numberOfFeatured
						? 'none'
						: 'some',
			initialIndustryOptions: selectedIndustryOptions,
			keywords: this.campaignEditor.template.keywords?.join(', ') || '',
			loadingContent: false,
			schedule: this.campaignEditor.template.schedule,

			selectedExpirationMoment,
			selectedIndustryOptions,
			showPreview: false,
			summary: this.campaignEditor.template.summary,

			testEmail: null,
		});
	};

	private setExpirationDay = (day: Date) => {
		const { selectedExpirationMoment } = this.state;
		const nextMoment = moment(day);

		// set the hours and minutes using the existing selected moment
		if (selectedExpirationMoment) {
			nextMoment.hours(selectedExpirationMoment.hours());
			nextMoment.minutes(selectedExpirationMoment.minutes());
		}

		this.setState({ selectedExpirationMoment: nextMoment });
	};

	private onFeaturedChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ featured: e.target.checked ? 'all' : 'none' });
	};

	private onExpiryChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ campaignExpires: e.target.checked });
		if (!e.target.checked) {
			this.setState({ selectedExpirationMoment: undefined });
		}
	};

	private get validSelectedHour() {
		const { selectedExpirationMoment } = this.state;
		const selectedHour = selectedExpirationMoment ? selectedExpirationMoment.get('hours') : null;
		const placeholder: ISelectBoxOption<number> = {
			isDefault: true,
			title: 'Select a time',
			value: -1,
		};
		return (selectedExpirationMoment ? this.timeOptions.find(x => x.value === selectedHour) : null) ?? placeholder;
	}

	private get timeOptions() {
		const { userSession } = this.props;
		const { selectedExpirationMoment } = this.state;

		// setting observeSendIntervals to false bypasses the business hrs constraints and allows for all times on all days to be shown
		const allOptions = getScheduleTimeOptions(
			this.mMinutes,
			userSession,
			{ observeSendIntervals: false },
			selectedExpirationMoment
		);
		return allOptions;
	}

	private get postImages() {
		return this.mEditSocialMediaPostContextRef.current?.postImages;
	}
	private get postContent() {
		return this.mEditSocialMediaPostContextRef.current?.postContent;
	}

	private onTimeOptionSelected = (option: ISelectBoxOption<number>) => {
		const { selectedExpirationMoment } = this.state;
		if (selectedExpirationMoment) {
			const nextSelectedMoment = moment(selectedExpirationMoment);

			nextSelectedMoment.set('hours', option.value);
			this.setState({
				selectedExpirationMoment: nextSelectedMoment,
			});
		}
	};

	private onContentEditorStateChanged = (contentEditorState: IRichContentEditorState) => {
		this.setState({ contentEditorState });
	};

	private onPreviewToggle = () => {
		this.setState({ showPreview: !this.state.showPreview });
	};

	public render() {
		const { className, styles } = this.props;
		const { loadingContent } = this.state;

		return (
			<div className={`${css(styleSheet.container, ...(styles || []))} edit-email-template ${className || ''}`}>
				<div className={css(styleSheet.containerInner)}>
					<EditSocialMediaPostContextProvider contextRef={this.mEditSocialMediaPostContextRef}>
						{loadingContent ? <LoadingSpinner type='large' /> : this.renderContent()}
					</EditSocialMediaPostContextProvider>
				</div>
			</div>
		);
	}

	private renderContent() {
		const {
			summary,
			keywords,
			contentEditorState,
			selectedIndustryOptions,
			featured,
			readonly,
			showPreview,
			campaignExpires,
			selectedExpirationMoment,
			templateLink,
		} = this.state;
		const titleText = `${readonly ? 'View' : this.isEditing ? 'Edit' : 'Create New'} Social Media Post`;

		return (
			<div>
				{this.isFinancialIndustry && !readonly && !this.isEditing && (
					<div>
						<label htmlFor='template-link-input' className={css(styleSheet.subjectFieldLabel)}>
							Template Link:
						</label>
						<TextInput
							autoComplete='off'
							className={css(styleSheet.fullWidthTextField)}
							inputId='template-link-input'
							onChange={this.onTemplateLinkedChanged}
							type='text'
							value={templateLink || ''}
							placeholder='Copy and paste the Direct link'
						/>
					</div>
				)}
				<EditCampaignTemplate.DisplayName readonly={readonly} titleText={titleText} />
				<div className={css(styleSheet.addMediaWrap)}>
					<SocialMediaPostContentEditor
						readOnly={readonly}
						imagesLabel='Add Media'
						messageLabel='Social Media Content'
					/>
				</div>
				<div className={css(styleSheet.buttonContainer)}>
					<div>
						<button className={css(styleSheet.buttonLink)} onClick={this.onPreviewToggle}>
							Preview
						</button>
					</div>
				</div>
				<Checkbox
					id='feature-campaign-checkbox'
					onChange={this.onFeaturedChanged}
					disabled={readonly}
					partial={featured === 'some'}
					checked={featured === 'all'}
					className={css(styleSheet.featureContainer)}
				>
					<span className={css(styleSheet.feature)}>Feature this campaign</span>
				</Checkbox>
				<ExpirationDisplay
					campaignExpires={campaignExpires}
					selectedExpirationMoment={selectedExpirationMoment}
					setExpirationDay={this.setExpirationDay}
					onTimeOptionSelected={this.onTimeOptionSelected}
					validSelectedHour={this.validSelectedHour}
					readonly={readonly}
					timeOptions={this.timeOptions}
					onExpiryChanged={this.onExpiryChanged}
				/>

				{!readonly ? (
					<>
						<IndustryCategorySection
							categoryOptions={this.getCategoryOptions()}
							selectedOptions={selectedIndustryOptions}
							onCheckboxChanged={this.onCheckboxChanged}
							onSelectBoxChanged={this.onSelectBoxChanged}
						/>
						<div className={css(styleSheet.sectionLine)} />
						<CitationEditor
							contentEditorState={contentEditorState}
							onContentEditorStateChanged={this.onContentEditorStateChanged}
						/>

						<div className={css(styleSheet.sectionLine)} />
						<div className={css(styleSheet.subHeader)}>Post Summary</div>
						<DeprecatedTextArea
							autoComplete='off'
							className={css(styleSheet.summaryTextField, styleSheet.fullWidthTextField)}
							readOnly={readonly}
							inputId='create-social-post-templates-summary-input'
							placeholder='Add Summary...'
							onChange={this.setInputValue('summary')}
							value={summary || ''}
						/>
						<div className={css(styleSheet.subHeader)}>Keywords</div>
						<TextInput
							autoComplete='off'
							className={css(styleSheet.fullWidthTextField)}
							readOnly={readonly}
							inputId='create-social-templates-keywords-input'
							placeholder='Keywords (separated by comma)'
							onChange={this.setInputValue('keywords')}
							type='text'
							value={keywords || ''}
						/>

						<footer className={css(styleSheet.footer)}>
							<div className={css(styleSheet.testSendContainer)}>
								{!this.isEditing && (
									<button
										className={css(baseStyleSheet.ctaButton)}
										disabled={this.campaignEditor.isBusy || !(featured === 'none')}
										onClick={this.onSave()}
									>
										<span>Save</span>
									</button>
								)}
								<button
									className={css(baseStyleSheet.ctaButtonReverse)}
									disabled={this.campaignEditor.isBusy}
									onClick={this.onSave(true)}
								>
									<span>Save & Publish</span>
								</button>
								{this.campaignEditor.isBusy && <LoadingSpinner type='small' />}
							</div>
						</footer>
					</>
				) : null}
				<PreviewSocialMediaPostModal
					selectedTargets={[
						{ provider: SocialMediaType.Facebook },
						{ provider: SocialMediaType.Instagram },
						{ provider: SocialMediaType.LinkedIn },
					]}
					modalProps={{
						isOpen: showPreview,
						onRequestClose: this.onPreviewToggle,
					}}
					impersonationCtx={this.props.impersonationContext}
					postContent={this.postContent}
					postImages={this.postImages}
				/>
			</div>
		);
	}

	private get isEditing() {
		return this.campaignEditor.isLoaded && !this.state.parentTemplateId;
	}

	private getCategoryOptions() {
		const { parentTemplateId } = this.state;

		// If it's a financial industry with a parent template ID, filter to only show BrokerDealer categories
		if (this.isFinancialIndustry && parentTemplateId) {
			const brokerDealerValues = Object.values(FinancialBrokerDealer);
			return this.mCategoryOptions
				.map(industry => ({
					...industry,
					categoryNames: industry.categoryNames?.filter(cat => brokerDealerValues.includes(cat as any)) || [],
				}))
				.filter(industry => industry.categoryNames?.length > 0);
		}

		return this.mCategoryOptions;
	}

	private onCheckboxChanged = (industry: Industry) => (e: React.ChangeEvent<HTMLInputElement>) => {
		const { selectedIndustryOptions: oldOptions } = this.state;
		if (!e.target.checked) {
			this.setState({
				selectedIndustryOptions: oldOptions?.filter(x => x.industry !== industry),
			});
		}
	};

	private onSelectBoxChanged = (industry: Industry) => (selectedOption: ISelectBoxOption<string>) => {
		const { selectedIndustryOptions: oldOptions } = this.state;
		/**
		 * @TODO take a look at where there is an issue with multiple selections
		 */
		// if (!firstSelection) {
		// 	const newList: ITemplateStatus[] = getCategoriesByName(this.mCategoryOptions, selectedOption.value);
		// 	this.setState({
		// 		selectedIndustryOptions: [...oldOptions, ...newList],
		// 		firstSelection: true,
		// 	});
		// 	return;
		// }
		const selectedIndustryOptions = oldOptions?.filter(x => x.industry !== industry);

		selectedIndustryOptions.push({
			category: selectedOption.value,
			industry,
		});

		this.setState({ selectedIndustryOptions });
	};

	private setInputValue = (key: keyof IState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		this.setState({
			[key]: e.target.value,
		});
	};

	private onSave =
		(publish = false) =>
		async () => {
			if (this.campaignEditor.isBusy) {
				return;
			}
			const {
				selectedIndustryOptions,
				featured,
				summary,
				keywords,
				initialIndustryOptions,
				selectedExpirationMoment,
				contentEditorState,
			} = this.state;
			const {
				errorMessages,
				logApiError,
				logInput,
				parentModal,
				postNotification,
				history,
				editCampaignTemplateContext,
			} = this.props;
			const { displayName } = editCampaignTemplateContext || {};

			const locationState: ILocationState<TemplateCategoriesViewModel, TemplateWithStatusesViewModel> =
				history?.location.state;

			let errorMessage: string = null;

			if (!displayName) {
				errorMessage = 'Template display name is empty.';
			}

			if (!selectedIndustryOptions.length) {
				errorMessage = 'You must select at least one industry-category pair';
			}

			if ((featured === 'some' || featured === 'all') && !publish) {
				errorMessage = 'You may not feature a template without publishing it';
			}

			if (errorMessage) {
				errorMessages.push({
					messages: [errorMessage],
				});
				return;
			}

			const validKeywords = (keywords || '')
				.split(',')
				.map(k => k.trim())
				.filter(k => !!k);

			let socialMedia: IEmbeddedSocialMediaTemplateContent = this.campaignEditor?.socialMedia
				? {
						...this.campaignEditor.socialMedia,
					}
				: null;
			if (this.mEditSocialMediaPostContextRef.current?.postContent) {
				if (!socialMedia) {
					socialMedia = {};
				}

				socialMedia.attachments = this.mEditSocialMediaPostContextRef.current.postImages;
				socialMedia.content = createRawRichTextContentStateWithText(
					this.mEditSocialMediaPostContextRef.current.postContent
				);
			}
			const template: ITemplate = {
				...(this.campaignEditor?.template || {}),
				attachments: socialMedia?.attachments,
				citation: getSocialMediaPlainTextContentFromRawRichContent(contentEditorState.getRawRichTextContent()),
				content: socialMedia?.content,
				keywords: validKeywords,
				name: displayName,
				summary,
				templateType: TemplateType.SocialMediaPost,
				parentTemplateId: this.state.parentTemplateId || null,
			};

			let schedule: ISuggestedSendSchedule = excludeKeysOf(template.schedule || {}, ['expirationDate']);
			if (selectedExpirationMoment) {
				schedule = {
					...schedule,
					expirationDate: selectedExpirationMoment.toISOString(),
				};
			}
			template.schedule = schedule;

			const onFinish = () => {
				const eventName = `${this.isEditing ? 'Edit' : 'Save'}${publish ? 'AndPublish' : ''}Template`;

				logInput(eventName, 'Click', { id: template.id });

				if (locationState?.viewModel) {
					locationState.viewModel.load()?.catch((err: IOperationResultNoValue) => {
						logApiError('RefreshTemplateCategory-Error', err);

						errorMessages.pushApiError(err);
					});
				}

				postNotification({
					info: this.campaignEditor.toJs(),
					topic: Topics.EDIT_SYSTEM_EMAIL_TEMPLATE,
				});

				parentModal?.onRequestClose(null, false);
			};

			if (!this.isEditing) {
				const templateWithStatuses: ISystemEmailTemplateWithStatuses = {
					template,
				};

				const statuses: ITemplateStatus[] = [];

				// generate statuses based on industries/categories/publishing/featured

				selectedIndustryOptions.forEach(o => {
					statuses.push({
						category: o.category,
						industry: o.industry,
						status: publish ? TemplateStatus.Published : TemplateStatus.Draft,
					});
					if (featured === 'all' && publish) {
						statuses.push({
							category: 'Featured',
							industry: o.industry,
							status: TemplateStatus.Published,
						});
					}
				});

				templateWithStatuses.statuses = statuses;
				this.campaignEditor
					.create(templateWithStatuses, CampaignType.Social)
					?.then(onFinish)
					?.catch((err: IOperationResultNoValue) => {
						logApiError('CreateSystemTemplate-Error', err);

						errorMessages.pushApiError(err);
					});
			} else {
				const statusesToRemove: ITemplateStatus[] = [];
				const statusesToAdd: ITemplateStatus[] = [];
				const statusesToUpdate: ITemplateStatus[] = [];
				// if they are editing instead of creating, they must publish
				this.mCategoryOptions.forEach(industry => {
					if (industry.categoryNames.length > 0) {
						const startStatusForIndustry = initialIndustryOptions.filter(x => x.industry === industry.industry)[0];

						const endStatusForIndustry = selectedIndustryOptions.filter(x => x.industry === industry.industry)[0];

						if (!startStatusForIndustry && endStatusForIndustry) {
							statusesToAdd.push({
								...endStatusForIndustry,
								status: TemplateStatus.Published,
							});
						} else if (!endStatusForIndustry && startStatusForIndustry) {
							statusesToRemove.push(startStatusForIndustry);
						} else if (startStatusForIndustry?.category !== endStatusForIndustry?.category) {
							statusesToRemove.push(startStatusForIndustry);
							statusesToAdd.push({
								...endStatusForIndustry,
								status: TemplateStatus.Published,
							});
						} else if (
							startStatusForIndustry?.status !== endStatusForIndustry?.status ||
							endStatusForIndustry?.status === TemplateStatus.Draft
						) {
							statusesToUpdate.push({
								...endStatusForIndustry,
								status: TemplateStatus.Published,
							});
						}
						// NEED TO UPDATE THIS
						// if featured checkbox is not changed, leave as is

						if (industry.categoryNames.includes('Featured')) {
							if (featured === 'none') {
								statusesToRemove.push({
									category: 'Featured',
									industry: industry.industry,
									status: TemplateStatus.Published,
								});
							} else if (featured === 'all' && endStatusForIndustry) {
								statusesToAdd.push({
									category: 'Featured',
									industry: industry.industry,
									status: TemplateStatus.Published,
								});
							}
						}
					}
				});

				this.campaignEditor
					.edit(
						template,
						statusesToRemove.filter(x => !!x),
						statusesToAdd.filter(x => !!x),
						statusesToUpdate.filter(x => !!x),
						CampaignType.Social
					)
					?.then(onFinish)
					?.catch((err: IOperationResultNoValue) => {
						logApiError('EditSystemTemplate-Error', err);

						errorMessages.pushApiError(err);
					});
			}
		};

	private get isFinancialIndustry() {
		// Extract industry from URL hash path
		const hashPath = window.location.hash || '';
		const pathSegments = hashPath.split('/');
		const hasFinancialInUrl = pathSegments.some(segment => segment.toLowerCase() === Industry.Financial.toLowerCase());
		return hasFinancialInUrl;
	}

	private extractTemplateIdFromLink(link: string): string {
		try {
			if (!link) return '';
			const match = link.match(/id=([^&]+)/);
			return match ? match[1] : '';
		} catch {
			return '';
		}
	}

	private async loadTemplateFromId(templateId: string) {
		if (!templateId) return;

		try {
			await this.campaignEditor.load(templateId, CampaignType.Social);
			this.onContentLoaded();
			this.setState({
				contentEditorState: createRichContentEditorStateWithText(this.campaignEditor?.template?.citation || ''),
				parentTemplateId: templateId,
				selectedIndustryOptions: [],
			});
		} catch (error) {
			this.props.errorMessages.pushApiError(error);
		}
	}

	private onTemplateLinkedChanged = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const link = e.target.value;
		const templateId = this.extractTemplateIdFromLink(link);

		this.setState({ templateLink: link });

		if (templateId) {
			await this.loadTemplateFromId(templateId);
		}
	};
}

const EditEmailTemplateAsObserver = observer(_EditSocialTemplate);
const EditSocialTemplateWithContext = inject(
	FullScreenModalViewModelKey,
	ErrorMessagesViewModelKey,
	UserSessionViewModelKey,
	ModalChildComponentContextKey,
	ImpersonationContextKey
)(EditEmailTemplateAsObserver);
const EditSocialTemplateWithLocalNotificationService = withNotificationService(EditSocialTemplateWithContext);
export const EditSocialTemplate = withRouter(
	withEventLogging(withEditCampaignTemplateContext(EditSocialTemplateWithLocalNotificationService), 'EditEmailTemplate')
);
